import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { take } from 'rxjs/internal/operators/take';
import { ClipAutoGroupingApiService } from '../../../shared/services/clip-auto-grouping-api.service';
import { SharedService } from '../../../shared/services/shared.service';
import { ClipGroupingDecision, ClipGroupingSettings } from '../../../shared/interfaces/clip-grouping.interfaces';

// Admin > Поставки > "Автоматско групирање" (Faza 1, feature/clip-auto-grouping). Disabled by
// default on the backend (ClipGroupingSettings entity) - this screen only lets an admin turn it
// on/tune it, never flips it itself. Turning it ON is confirmed (same convention as
// AutotagSettingsComponent's toggle confirm) since it starts writing groupId/main on live clips.
@Component({
	selector: 'app-clip-auto-grouping-settings',
	standalone: true,
	imports: [CommonModule, FormsModule, MatButtonModule, MatInputModule, MatFormFieldModule, MatSlideToggleModule, MatProgressSpinnerModule, MatIconModule, MatTableModule],
	templateUrl: './clip-auto-grouping-settings.component.html',
	styleUrl: './clip-auto-grouping-settings.component.scss',
})
export class ClipAutoGroupingSettingsComponent implements OnInit {
	settings: ClipGroupingSettings | null = null;
	loading = false;
	saving = false;

	displayedColumns: string[] = ['clipTitle', 'decision', 'bestSimilarity', 'bestCandidateMainClipTitle', 'processedAt'];
	dataSource = new MatTableDataSource<ClipGroupingDecision>();
	loadingDecisions = false;

	constructor(
		private api: ClipAutoGroupingApiService,
		private sharedService: SharedService
	) {}

	ngOnInit(): void {
		this.load();
		this.loadDecisions();
	}

	load(): void {
		this.loading = true;
		this.api
			.getSettings()
			.pipe(take(1))
			.subscribe({
				next: settings => {
					this.settings = settings;
					this.loading = false;
				},
				error: () => {
					this.loading = false;
					this.sharedService.showNotification.next({ message: 'Не успеа вчитувањето на поставките.', type: 'ERROR' });
				},
			});
	}

	loadDecisions(): void {
		this.loadingDecisions = true;
		this.api
			.getRecentDecisions(200)
			.pipe(take(1))
			.subscribe({
				next: decisions => {
					this.dataSource.data = decisions || [];
					this.loadingDecisions = false;
				},
				error: () => {
					this.loadingDecisions = false;
				},
			});
	}

	onEnabledToggle(checked: boolean): void {
		if (!this.settings) {
			return;
		}
		if (checked && !confirm('Сигурни ли сте? Системот ќе почне автоматски да спојува нови вести во групи.')) {
			return;
		}
		this.settings.enabled = checked;
		this.save();
	}

	save(): void {
		if (!this.settings) {
			return;
		}
		this.saving = true;
		this.api
			.updateSettings(this.settings)
			.pipe(take(1))
			.subscribe({
				next: settings => {
					this.settings = settings;
					this.saving = false;
					this.sharedService.showNotification.next({ message: 'Поставките се зачувани.', type: 'SUCCESS' });
				},
				error: () => {
					this.saving = false;
					this.sharedService.showNotification.next({ message: 'Не успеа зачувувањето.', type: 'ERROR' });
				},
			});
	}

	decisionLabel(decision: string): string {
		switch (decision) {
			case 'MERGED':
				return 'Споена';
			case 'NEW_GROUP':
				return 'Нова група';
			case 'DUPLICATE':
				return 'Дупликат';
			case 'KEPT':
				return 'Остана во група';
			default:
				return decision;
		}
	}

	formatSimilarity(value: number | null | undefined): string {
		if (value === null || value === undefined) {
			return '—';
		}
		return `${Math.round(value * 100)}%`;
	}

	formatDate(value: string | null | undefined): string {
		if (!value) {
			return '—';
		}
		return new Date(value).toLocaleString('mk-MK');
	}
}
